import React, { useEffect, useState } from "react";
import axios from "axios";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, Cell, LineChart, Line
} from "recharts";
import {
    Star as StarIcon, MessageSquare, UserCheck, Clock,
    TrendingUp, AlertCircle, CheckCircle, ThumbsUp,
    Award
} from 'lucide-react';

const Ratings = ({ isMobile }) => {

    /* -------------------- STATE -------------------- */

    const [ratingsData, setRatingsData] = useState(null);
    const [loading, setLoading] = useState(true);

    /* -------------------- API CALL -------------------- */

    useEffect(() => {
        const fetchRatings = async () => {
            try {
                const vendorId = localStorage.getItem("vendorId") || 1;
                const token = localStorage.getItem("authToken"); // IMPORTANT

                const res = await axios.get(
                    "http://testing.maamaas.com:8080/food/api/orders/vendor/statistics/custom",
                    {
                        params: {
                            vendorId,
                            fromDate: "2025-12-17",
                            toDate: "2025-12-18"
                        },
                        headers: {
                            Authorization: token ? `Bearer ${token}` : undefined
                        }
                    }
                );

                console.log("✅ API RESPONSE:", res.data); // 🔥 CHECK THIS

                const data = res.data;

                const totalReviews = data.totalRatings || 0;
                const avgRating = Number(data.averageRating || 0);

                const distribution = [
                    { label: "5★", count: 0, color: "#10b981" },
                    { label: "4★", count: 0, color: "#3b82f6" },
                    { label: "3★", count: 0, color: "#f59e0b" },
                    { label: "2★", count: 0, color: "#f97316" },
                    { label: "1★", count: 0, color: "#ef4444" }
                ];

                const trend = data.dailyStats?.map(day => ({
                    date: day.date,
                    avgRating: avgRating
                })) || [];

                setRatingsData({
                    distribution,
                    trend,
                    summary: {
                        overallRating: avgRating.toFixed(1),
                        totalReviews,
                        responseRate: 95,
                        avgResponseTime: "45 mins"
                    },
                    feedbackCategories: [
                        { category: "Food Quality", rating: avgRating, reviews: totalReviews, color: "#10b981" },
                        { category: "Delivery", rating: avgRating, reviews: totalReviews, color: "#3b82f6" },
                        { category: "Packaging", rating: avgRating, reviews: totalReviews, color: "#f59e0b" }
                    ],
                    commonIssues: []
                });

            } catch (err) {
                console.error("❌ Ratings API Error");
                console.error("Status:", err.response?.status);
                console.error("Data:", err.response?.data);
                console.error("Message:", err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchRatings();
    }, []);


    if (loading || !ratingsData) {
        return <div style={{ padding: 40 }}>Loading ratings...</div>;
    }

    /* -------------------- SUMMARY CARDS -------------------- */

    const RatingsSummaryCards = () => (
        <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap: '12px',
            marginBottom: '20px'
        }}>
            {[
                { title: "Rating", value: `${ratingsData.summary.overallRating}/5`, icon: <StarIcon />, color: "#f59e0b" },
                { title: "Reviews", value: ratingsData.summary.totalReviews, icon: <MessageSquare />, color: "#3b82f6" },
                { title: "Response Rate", value: `${ratingsData.summary.responseRate}%`, icon: <UserCheck />, color: "#10b981" },
                { title: "Response Time", value: ratingsData.summary.avgResponseTime, icon: <Clock />, color: "#8b5cf6" }
            ].map((card, i) => (
                <div key={i} style={{
                    background: "#fff",
                    padding: 16,
                    borderRadius: 12,
                    border: "1px solid #e2e8f0",
                    display: "flex",
                    justifyContent: "space-between"
                }}>
                    <div>
                        <div style={{ fontSize: 12, color: "#64748b" }}>{card.title}</div>
                        <div style={{ fontSize: 20, fontWeight: 800 }}>{card.value}</div>
                    </div>
                    <div style={{ color: card.color }}>{card.icon}</div>
                </div>
            ))}
        </div>
    );

    /* -------------------- DISTRIBUTION -------------------- */

    const RatingsAnalysis = () => (
        <div style={{
            background: "#fff",
            borderRadius: 24,
            padding: 28,
            border: "1px solid #e2e8f0",
            marginBottom: 28
        }}>
            <h3 style={{ fontWeight: 800 }}>Ratings Distribution</h3>

            <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={ratingsData.distribution}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count">
                            {ratingsData.distribution.map((e, i) => (
                                <Cell key={i} fill={e.color} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );

    /* -------------------- TREND -------------------- */

    const RatingTrendChart = () => (
        <div style={{
            background: "#fff",
            borderRadius: 24,
            padding: 28,
            border: "1px solid #e2e8f0"
        }}>
            <h3 style={{ fontWeight: 800 }}>Rating Trend</h3>

            <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={ratingsData.trend}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis domain={[0, 5]} />
                        <Tooltip />
                        <Line
                            type="monotone"
                            dataKey="avgRating"
                            stroke="#f59e0b"
                            strokeWidth={3}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );

    /* -------------------- RENDER -------------------- */

    return (
        <>
            <RatingsSummaryCards />
            <RatingsAnalysis />
            <RatingTrendChart />
        </>
    );
};

export default Ratings;
